import { resolveFrenchCityLabelFromCoordinates } from "./frenchCityLookup";
import { sanitizeSingleLineText } from "./textSecurity";

const DEFAULT_REVERSE_GEOCODE_ENDPOINT =
  "https://nominatim.openstreetmap.org/reverse";
const reverseGeocodeCache = new Map();

function getSafeCoordinates(coordinates) {
  if (!Array.isArray(coordinates) || coordinates.length !== 2) {
    return null;
  }

  const [lng, lat] = coordinates;

  if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
    return null;
  }

  return [Number(lng), Number(lat)];
}

function getReverseGeocodeEndpoint() {
  return String(process.env.REACT_APP_REVERSE_GEOCODE_URL || "").trim();
}

function normalizeLabel(...candidates) {
  return candidates
    .map((value) => {
      if (typeof value !== "string" && typeof value !== "number") {
        return "";
      }

      return sanitizeSingleLineText(value || "");
    })
    .find(Boolean) || "";
}

function buildCacheKey(coordinates) {
  const safeCoordinates = getSafeCoordinates(coordinates);

  if (!safeCoordinates) {
    return "";
  }

  return safeCoordinates.map((value) => Number(value).toFixed(5)).join(",");
}

function buildFallbackLocation(coordinates, fallbackCityLabel = "") {
  const city = normalizeLabel(
    fallbackCityLabel,
    resolveFrenchCityLabelFromCoordinates(coordinates)
  );

  return {
    city,
    address: "",
    label: city ? `Zone autour de ${city}` : "",
    resolved: false,
    source: "fallback",
  };
}

function buildAddressLine(address = {}) {
  const mainLine = [
    address.house_number,
    address.road || address.pedestrian || address.footway || address.path,
  ]
    .map((value) => normalizeLabel(value))
    .filter(Boolean)
    .join(" ");

  return normalizeLabel(
    mainLine,
    address.neighbourhood,
    address.suburb,
    address.hamlet
  );
}

function normalizeReverseGeocodeResponse(payload = {}) {
  const addressObject =
    payload.address && Object.getPrototypeOf(payload.address) === Object.prototype
      ? payload.address
      : {};
  const addressLine = buildAddressLine(addressObject);
  const city = normalizeLabel(
    payload.city,
    payload.town,
    payload.village,
    payload.municipality,
    payload.locality,
    addressObject.city,
    addressObject.town,
    addressObject.village,
    addressObject.municipality,
    addressObject.county
  );
  const address = normalizeLabel(
    addressLine,
    typeof payload.address === "string" ? payload.address : "",
    payload.addressLabel,
    payload.formattedAddress,
    payload.displayName,
    payload.display_name,
    payload.name
  );
  const label = normalizeLabel(
    payload.label,
    payload.name,
    payload.poi,
    address
  );

  return {
    city,
    address,
      label,
  };
}

async function fetchCustomReverseGeocode(endpoint, coordinates) {
  const [lng, lat] = coordinates;
  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ lng, lat }),
  });

  if (!response.ok) {
    throw new Error(`Reverse geocoding error: ${response.status}`);
  }

  return response.json();
}

async function fetchDefaultReverseGeocode(coordinates) {
  const [lng, lat] = coordinates;
  const query = new URLSearchParams({
    lat: String(lat),
    lon: String(lng),
    format: "jsonv2",
    addressdetails: "1",
  });
  const response = await fetch(
    `${DEFAULT_REVERSE_GEOCODE_ENDPOINT}?${query.toString()}`,
    {
      headers: {
        Accept: "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Reverse geocoding error: ${response.status}`);
  }

  return response.json();
}

export async function reverseGeocodeCoordinates(
  coordinates,
  { fallbackCityLabel = "" } = {}
) {
  const safeCoordinates = getSafeCoordinates(coordinates);

  if (!safeCoordinates) {
    return buildFallbackLocation(null, fallbackCityLabel);
  }

  const cacheKey = buildCacheKey(safeCoordinates);

  if (cacheKey && reverseGeocodeCache.has(cacheKey)) {
    return reverseGeocodeCache.get(cacheKey);
  }

  const endpoint = getReverseGeocodeEndpoint();

  try {
    const payload = endpoint
      ? await fetchCustomReverseGeocode(endpoint, safeCoordinates)
      : await fetchDefaultReverseGeocode(safeCoordinates);
    const normalizedPayload = normalizeReverseGeocodeResponse(payload);
    const fallbackLocation = buildFallbackLocation(
      safeCoordinates,
      fallbackCityLabel
    );

    const result = {
      city: normalizedPayload.city || fallbackLocation.city,
      address: normalizedPayload.address,
      label: normalizedPayload.label || normalizedPayload.address || fallbackLocation.label,
      resolved: Boolean(normalizedPayload.city || normalizedPayload.address),
      source: endpoint ? "remote" : "public",
    };

    if (cacheKey) {
      reverseGeocodeCache.set(cacheKey, result);
    }

    return result;
  } catch (error) {
    console.error(error);
    return buildFallbackLocation(safeCoordinates, fallbackCityLabel);
  }
}
